/**
 * Practice Session Hook
 *
 * Manages practice session state, mode launching, assessment flow,
 * and related UI state (module panel, cheat sheet, validation errors).
 *
 * Moved from src/hooks/usePracticeHandlers.ts in R31 (DDD Fase 4).
 * Intra-domain import: ../logic/sessionValidation (was ../../utils/sessionValidation)
 */

import { useState, useCallback } from 'react'
import {
  type ModuleId,
  type Question,
  type ModuleData,
  getAllQuestions,
} from '../../content'
import { AppState } from '../../app/types'
import { GroupId } from '../../config/systemConfig'
import { createModeSession } from '../../modes'
import type {
  FluencyConfig,
  SprintConfig,
  ChallengeConfig,
  ExperimentsConfig,
  ModeId as ModeIdType,
} from '../../modes'
import { getWeakestFamiliesGlobal } from '../../mastery'
import {
  validateSessionAvailability,
  logValidationReport,
} from '../logic/sessionValidation'

import { debug, warn, error as logError } from '../../utils/logger'
import { useEscapeKey } from '../../app/hooks/useEscapeKey'

interface UsePracticeHandlersParams {
  state: AppState
  selectedGroup: GroupId
  markCheatSheetViewed: (moduleId: ModuleId) => void
  enterPracticeView: () => void
  resetToDashboard: () => void
  resetToLearningLab: () => void
}

export function usePracticeHandlers({
  state,
  selectedGroup,
  markCheatSheetViewed,
  enterPracticeView,
  resetToDashboard,
  resetToLearningLab,
}: UsePracticeHandlersParams) {
  const [activeModuleId, setActiveModuleId] = useState<ModuleId | null>(null)
  const [activeModeId, setActiveModeId] = useState<string | null>(null)
  const [isFocusSession, setIsFocusSession] = useState(false)
  const [focusQuestions, setFocusQuestions] = useState<Question[]>([])
  const [selectedModule, setSelectedModule] = useState<ModuleData | null>(null)
  const [cheatSheetModuleId, setCheatSheetModuleId] = useState<ModuleId | null>(
    null,
  )
  const [validationError, setValidationError] = useState<{
    moduleId: ModuleId
    message: string
    hasContent: boolean
  } | null>(null)

  // Keyboard navigation (Escape to close panels)
  const handleEscapeClose = useCallback(() => {
    if (cheatSheetModuleId) setCheatSheetModuleId(null)
    else if (selectedModule) setSelectedModule(null)
  }, [cheatSheetModuleId, selectedModule])
  useEscapeKey(handleEscapeClose, !!(cheatSheetModuleId || selectedModule))

  const handleStartPractice = useCallback(
    (moduleId: ModuleId) => {
      const validationReport = validateSessionAvailability(
        moduleId,
        selectedGroup,
      )
      if (!validationReport.isValid) {
        if (process.env.NODE_ENV === 'development') {
          warn(
            `Cannot start practice for ${moduleId} in group ${selectedGroup}:`,
          )
          logValidationReport(validationReport)
        }
        setValidationError({
          moduleId,
          message: validationReport.errors[0] || 'Geen vragen beschikbaar',
          hasContent: validationReport.stats.totalFamilies > 0,
        })
        return
      }
      if (process.env.NODE_ENV === 'development') {
        debug(
          `Pre-flight check passed for ${moduleId} in group ${selectedGroup}`,
        )
        debug(
          `   ${validationReport.stats.totalQuestionsAvailable} questions available from ${validationReport.stats.familiesWithQuestions} families`,
        )
      }
      markCheatSheetViewed(moduleId)
      setActiveModuleId(moduleId)
      enterPracticeView()
      setIsFocusSession(false)
      setSelectedModule(null)
    },
    [selectedGroup, markCheatSheetViewed, enterPracticeView],
  )

  const handlePracticeComplete = useCallback(() => {
    const wasFocusSession = isFocusSession
    setActiveModuleId(null)
    setActiveModeId(null)
    setIsFocusSession(false)
    setFocusQuestions([])
    // Focus sessions (modes) always start from LearningLab — navigate back there
    if (wasFocusSession) {
      resetToLearningLab()
    } else {
      resetToDashboard()
    }
  }, [isFocusSession, resetToDashboard, resetToLearningLab])

  const exitPractice = useCallback(() => {
    const wasFocusSession = isFocusSession
    setActiveModuleId(null)
    setActiveModeId(null)
    setIsFocusSession(false)
    setFocusQuestions([])
    if (wasFocusSession) {
      resetToLearningLab()
    } else {
      resetToDashboard()
    }
  }, [isFocusSession, resetToDashboard, resetToLearningLab])

  // Start a focus session (assessment or mode) with pre-selected questions
  const startFocusSession = useCallback(
    (questions: Question[]) => {
      setActiveModuleId(null)
      setIsFocusSession(true)
      setFocusQuestions(questions)
      enterPracticeView()
    },
    [enterPracticeView],
  )

  const handleStartMode = useCallback(
    (
      modeId: string,
      config?: { targetFamilyId?: string; targetModules?: string[] },
    ) => {
      try {
        let modeConfig:
          | FluencyConfig
          | SprintConfig
          | ChallengeConfig
          | ExperimentsConfig
        switch (modeId) {
          case 'fluency':
            modeConfig = {
              duration: 300,
              targetModules: config?.targetModules || null,
            } as FluencyConfig
            break
          case 'sprint': {
            let targetFamilyId = config?.targetFamilyId
            if (!targetFamilyId) {
              const weakFamilies = getWeakestFamiliesGlobal(
                state.familyMastery || {},
                selectedGroup,
                1,
              )
              if (weakFamilies.length === 0) {
                setValidationError({
                  moduleId: 'sprint' as ModuleId,
                  message:
                    'Je hebt nog geen onderwerpen geoefend. Begin eerst met een vak!',
                  hasContent: false,
                })
                return
              }
              targetFamilyId = weakFamilies[0].familyId
            }
            modeConfig = {
              targetFamilyId,
              targetCount: 10,
              adaptive: true,
              warmup: true,
            } as SprintConfig
            break
          }
          case 'challenge': {
            let targetFamilyId = config?.targetFamilyId
            if (!targetFamilyId) {
              const familyMastery = state.familyMastery || {}
              const strongFamilies = Object.entries(familyMastery)
                .filter(([_, m]) => m.masteryScore >= 70)
                .sort((a, b) => b[1].masteryScore - a[1].masteryScore)
              if (strongFamilies.length === 0) {
                setValidationError({
                  moduleId: 'challenge' as ModuleId,
                  message:
                    'Je hebt nog geen onderwerpen goed genoeg beheerst (70%+). Oefen eerst meer!',
                  hasContent: false,
                })
                return
              }
              targetFamilyId = strongFamilies[0][0].replace(/_g\d+$/, '')
            }
            modeConfig = {
              targetFamilyId,
              difficulty: 2,
            } as ChallengeConfig
            break
          }
          case 'experiments':
            modeConfig = {
              experimentType: 'mixed',
            } as ExperimentsConfig
            break
          case 'free': {
            // Free practice: load ALL questions for a family, ignoring group/tier filters
            const freeFamilyId = config?.targetFamilyId
            if (!freeFamilyId) return
            const allQuestions = getAllQuestions()
            const familyQuestions = allQuestions.filter(
              (q) => q.familyId === freeFamilyId,
            )
            if (familyQuestions.length === 0) {
              setValidationError({
                moduleId: 'free' as ModuleId,
                message: 'Geen vragen beschikbaar voor dit onderwerp.',
                hasContent: false,
              })
              return
            }
            // Shuffle and take up to 10
            const shuffled = [...familyQuestions].sort(
              () => Math.random() - 0.5,
            )
            startFocusSession(shuffled.slice(0, 10))
            return
          }
          default:
            return
        }
        const session = createModeSession(
          modeId as ModeIdType,
          modeConfig as FluencyConfig | SprintConfig | ChallengeConfig,
          state,
          selectedGroup,
        )
        setActiveModeId(modeId)
        startFocusSession(session.questions)
      } catch (err) {
        logError('Failed to start mode session:', err)
        setValidationError({
          moduleId: modeId as ModuleId,
          message:
            'Geen vragen beschikbaar voor deze trainingsmodus. Probeer een ander vak te oefenen.',
          hasContent: false,
        })
      }
    },
    [state, selectedGroup, startFocusSession],
  )

  return {
    activeModuleId,
    activeModeId,
    isFocusSession,
    focusQuestions,
    selectedModule,
    setSelectedModule,
    cheatSheetModuleId,
    setCheatSheetModuleId,
    validationError,
    setValidationError,
    handleStartPractice,
    handlePracticeComplete,
    handleStartMode,
    exitPractice,
    startFocusSession,
  }
}
